import { playerId } from './storage';

/**
 * Lean, opt-out-able telemetry. Batches typed events to POST /api/events
 * every 30 s and on page hide (sendBeacon). No canvas snapshots, no farm
 * names as ids, nothing if the player opts out in Settings.
 */

export type TelemetryEvent =
  | { type: 'session_start'; fresh: boolean; version: string }
  | { type: 'purchase'; item: string; cost: number; flock?: number }
  | { type: 'rooster'; n: number; breed: string; atSec: number }
  | { type: 'win'; atSec: number }
  | { type: 'welcome_back'; awayMs: number; earned: number; capped: boolean }
  | { type: 'achievement'; id: string }
  | { type: 'error'; message: string }
  | { type: 'feedback'; category: string };

interface Envelope { t: number; e: TelemetryEvent }

const QUEUE_MAX = 500;
const FLUSH_MS = 30_000;
let queue: Envelope[] = [];
let enabled = true;
let sessionId = '';
let timer: number | undefined;

export function initTelemetry(optIn: boolean): void {
  enabled = optIn;
  sessionId = Math.random().toString(36).slice(2, 10);
  if (timer === undefined) timer = window.setInterval(() => void flush(), FLUSH_MS);
  window.addEventListener('pagehide', beacon);
  window.addEventListener('error', (ev) => track({ type: 'error', message: String(ev.message).slice(0, 200) }));
}

export function setTelemetry(on: boolean): void {
  enabled = on;
  if (!on) queue = [];
}

export function track(e: TelemetryEvent): void {
  if (!enabled) return;
  queue.push({ t: Date.now(), e });
  if (queue.length > QUEUE_MAX) queue.splice(0, queue.length - QUEUE_MAX);
}

function payload(): string | null {
  if (!enabled || queue.length === 0) return null;
  const body = JSON.stringify({ player: playerId(), session: sessionId, events: queue });
  queue = [];
  return body;
}

export async function flush(): Promise<void> {
  const body = payload();
  if (!body) return;
  try {
    await fetch('./api/events', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
  } catch {
    /* offline or no server (static hosting) — telemetry is best-effort */
  }
}

function beacon(): void {
  const body = payload();
  if (body && navigator.sendBeacon) navigator.sendBeacon('./api/events', new Blob([body], { type: 'application/json' }));
}

export async function sendFeedback(category: string, text: string, context: Record<string, unknown>): Promise<boolean> {
  try {
    const res = await fetch('./api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ player: playerId(), category, text: text.slice(0, 4000), context }),
    });
    track({ type: 'feedback', category });
    return res.ok;
  } catch {
    return false;
  }
}
